import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Trash2, 
  Edit2, 
  X, 
  Check,
  AlertCircle,
  Percent
} from 'lucide-react';
import { 
  db, 
  auth,
  collection, 
  onSnapshot, 
  query, 
  where, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs,
  serverTimestamp,
  handleFirestoreError,
  OperationType
} from '../firebase';
import { cn } from '../lib/utils';
import { notify } from '../services/notify';

const getLocalAuthToken = () =>
  typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;

const isLocalSession = () => Boolean(getLocalAuthToken());

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = getLocalAuthToken();
  if (!token) throw new Error('Local session token is unavailable');

  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    throw new Error((await response.text()) || `Request failed (${response.status})`);
  }

  return response.status === 204 ? (undefined as T) : response.json();
};

const localApiTaxRates = (payload: any): TaxRate[] =>
  Array.isArray(payload) ? payload : payload?.taxRates || payload?.rates || payload?.data || [];

const localApiTaxRate = (payload: any): TaxRate =>
  (payload?.taxRate || payload?.rate || payload?.data || payload) as TaxRate;

interface TaxRate {
  id: string;
  name: string;
  rate: number;
  type?: 'sales' | 'purchases' | 'both';
  description?: string;
  orgId: string;
  mappedAccountId?: string;
  createdAt: any;
}

interface Account {
  id: string;
  code: string;
  name: string;
  type: string;
}

interface TaxBaseViewProps {
  orgId: string;
  theme: any;
  language: 'en' | 'ar';
}

export default function TaxBaseView({ orgId,  theme, language = 'en'}: TaxBaseViewProps) {
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTax, setEditingTax] = useState<TaxRate | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    rate: number;
    type: 'sales' | 'purchases' | 'both';
    description: string;
    mappedAccountId: string;
  }>({
    name: '',
    rate: 0,
    type: 'both',
    description: '',
    mappedAccountId: ''
  });

  useEffect(() => {
    if (!orgId) return;

    const localSession = isLocalSession();
    let cancelled = false;
    let unsubscribe = () => {};

    if (localSession) {
      localApiRequest<unknown>(`/api/organizations/${orgId}/tax-rates`)
        .then((payload) => {
          if (!cancelled) {
            setTaxRates([...localApiTaxRates(payload)].sort((a, b) => (a.rate || 0) - (b.rate || 0)));
          }
        })
        .catch((error) => {
          if (!cancelled) {
            console.error('Error loading tax rates:', error);
            notify.error(language === 'ar' ? 'فشل في تحميل الضرائب' : 'Failed to load tax rates');
          }
        });
    } else {
      const q = query(
        collection(db, 'tax_rates'),
        where('orgId', '==', orgId)
      );

      unsubscribe = onSnapshot(q, (snapshot) => {
        const rates = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as TaxRate[];
        setTaxRates([...rates].sort((a, b) => (a.rate || 0) - (b.rate || 0)));
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, 'tax_rates');
      });
    }

    let unsubscribeAccounts = () => {};
    if (localSession) {
      localApiRequest<Account[]>(`/api/organizations/${orgId}/chart-of-accounts`)
        .then((loadedAccounts) => {
          if (!cancelled) setAccounts(Array.isArray(loadedAccounts) ? loadedAccounts : []);
        })
        .catch((error) => {
          if (!cancelled) {
            console.error('Error loading chart of accounts:', error);
            notify.error(language === 'ar' ? 'فشل في تحميل الحسابات' : 'Failed to load chart of accounts');
          }
        });
    } else {
      const accountsQ = query(collection(db, 'chart_of_accounts'), where('orgId', '==', orgId));
      unsubscribeAccounts = onSnapshot(accountsQ, (snapshot) => {
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Account));
        setAccounts(docs);
        if (docs.length === 0 && !isModalOpen) seedBasicAccounts();
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, 'chart_of_accounts');
      });
    }

    return () => {
      cancelled = true;
      unsubscribe();
      unsubscribeAccounts();
    };
  }, [orgId]);

  const seedBasicAccounts = async () => {
    if (!orgId || !auth.currentUser) return;
    const { CORE_CHART_OF_ACCOUNTS } = await import('../constants');
    
    const basicAccounts = CORE_CHART_OF_ACCOUNTS.map(acc => ({
      ...acc,
      isSystemAccount: true,
      status: 'active' as const
    }));

    const path = 'chart_of_accounts';
    const userName = auth.currentUser.displayName || auth.currentUser.email?.split('@')[0] || 'System';

    try {
      // 1. Seed Chart of Accounts
      const coaQ = query(collection(db, path), where('orgId', '==', orgId));
      const coaSnap = await getDocs(coaQ);
      const accountsByCode: Record<string, string> = {};
      coaSnap.docs.forEach(d => {
        accountsByCode[d.data().code] = d.id;
      });

      let coaChanged = false;
      for (const acc of basicAccounts) {
        if (!accountsByCode[acc.code]) {
          const docRef = await addDoc(collection(db, path), {
            ...acc,
            orgId,
            createdBy: auth.currentUser.uid,
            createdByName: userName,
            createdAt: serverTimestamp()
          });
          accountsByCode[acc.code] = docRef.id;
          coaChanged = true;
        }
      }
      if (coaChanged) notify.confirm("Chart of Accounts initialized");

      // 2. Seed Tax Rates
      const taxQ = query(collection(db, 'tax_rates'), where('orgId', '==', orgId));
      const taxSnap = await getDocs(taxQ);
      if (taxSnap.empty) {
        const defaultTaxes = [
          { name: 'VAT 15%', rate: 15, type: 'both', description: 'Standard VAT Rate', mappedAccountId: accountsByCode['2100'] || '' },
          { name: 'VAT 0%', rate: 0, type: 'both', description: 'Zero Rated VAT', mappedAccountId: accountsByCode['2100'] || '' },
          { name: 'Exempt', rate: 0, type: 'both', description: 'Tax Exempt', mappedAccountId: '' }
        ];

        for (const tax of defaultTaxes) {
          await addDoc(collection(db, 'tax_rates'), {
            ...tax,
            orgId,
            createdAt: serverTimestamp()
          });
        }
        notify.confirm("Standard tax rates initialized");
      }
    } catch (err) {
      console.error("Error seeding data in TaxBaseView:", err);
      notify.error("Failed to seed initial data");
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || formData.rate === undefined) {
      notify.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill in all required fields');
      return;
    }

    try {
      if (isLocalSession()) {
        if (editingTax) {
          const response = await localApiRequest<unknown>(`/api/organizations/${orgId}/tax-rates/${editingTax.id}`, {
            method: 'PATCH',
            body: JSON.stringify(formData),
          });
          const updatedTax = localApiTaxRate(response);
          setTaxRates(current => current
            .map(tax => tax.id === editingTax.id ? { ...tax, ...formData, ...updatedTax } as TaxRate : tax)
            .sort((a, b) => (a.rate || 0) - (b.rate || 0)));
          notify.confirm(language === 'ar' ? 'تم تحديث الضريبة بنجاح' : 'Tax rate updated successfully');
        } else {
          const response = await localApiRequest<unknown>(`/api/organizations/${orgId}/tax-rates`, {
            method: 'POST',
            body: JSON.stringify(formData),
          });
          const createdTax = localApiTaxRate(response);
          if (createdTax?.id) {
            setTaxRates(current => [...current, { ...formData, ...createdTax, orgId } as TaxRate]
              .sort((a, b) => (a.rate || 0) - (b.rate || 0)));
          }
          notify.confirm(language === 'ar' ? 'تم إضافة الضريبة بنجاح' : 'Tax rate added successfully');
        }
      } else if (editingTax) {
        await updateDoc(doc(db, 'tax_rates', editingTax.id), {
          ...formData,
          updatedAt: serverTimestamp()
        });
        notify.confirm(language === 'ar' ? 'تم تحديث الضريبة بنجاح' : 'Tax rate updated successfully');
      } else {
        await addDoc(collection(db, 'tax_rates'), {
          ...formData,
          orgId,
          createdAt: serverTimestamp()
        });
        notify.confirm(language === 'ar' ? 'تم إضافة الضريبة بنجاح' : 'Tax rate added successfully');
      }
      closeModal();
    } catch (error) {
      console.error(error);
      notify.error(language === 'ar' ? 'فشل في حفظ الضريبة' : 'Failed to save tax rate');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذه الضريبة؟' : 'Are you sure you want to delete this tax rate?')) return;
    try {
      if (isLocalSession()) {
        await localApiRequest<void>(`/api/organizations/${orgId}/tax-rates/${id}`, { method: 'DELETE' });
        setTaxRates(current => current.filter(tax => tax.id !== id));
      } else {
        await deleteDoc(doc(db, 'tax_rates', id));
      }
      notify.confirm(language === 'ar' ? 'تم حذف الضريبة بنجاح' : 'Tax rate deleted successfully');
    } catch (error) {
      console.error(error);
      notify.error(language === 'ar' ? 'فشل في حذف الضريبة' : 'Failed to delete tax rate');
    }
  };

  const openModal = (tax?: TaxRate) => {
    if (tax) {
      setEditingTax(tax);
      setFormData({
        name: tax.name,
        rate: tax.rate,
        type: tax.type || 'both',
        description: tax.description || '',
        mappedAccountId: tax.mappedAccountId || ''
      });
    } else {
      setEditingTax(null);
      setFormData({
        name: '',
        rate: 0,
        type: 'both',
        description: '',
        mappedAccountId: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTax(null);
    setFormData({
      name: '',
      rate: 0,
      type: 'both',
      description: '',
      mappedAccountId: ''
    });
  };

  const filteredRates = taxRates.filter(tax => 
    (tax.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (tax.rate || 0).toString().includes(searchQuery)
  );

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-bold italic serif tracking-tight mb-2">
            {language === 'ar' ? 'قاعدة ضريبة القيمة المضافة' : 'VAT Base'}
          </h1>
          <p className="opacity-50 italic serif">
            {language === 'ar' ? 'إدارة معدلات ضريبة القيمة المضافة المستخدمة في النظام' : 'Manage VAT rates used across the system'}
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className={cn(
            "flex items-center gap-2 px-6 py-3 font-bold uppercase tracking-widest transition-all hover:scale-105 active:scale-95",
            theme.accent,
            theme.accentText,
            theme.rounded
          )}
        >
          <Plus className="w-5 h-5" />
          {language === 'ar' ? 'إضافة ضريبة جديدة' : 'Add New VAT Rate'}
        </button>
      </div>

      <div className={cn("p-6 border-2 mb-8", theme.border, theme.rounded, theme.card)}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 opacity-40" />
          <input
            type="text"
            placeholder={language === 'ar' ? 'البحث عن طريق الاسم أو المعدل...' : 'Search by name or rate...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-4 bg-transparent border-none outline-none text-lg italic serif"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredRates.map((tax) => (
          <div 
            key={tax.id}
            className={cn(
              "p-6 border-2 transition-all hover:shadow-xl group",
              theme.border,
              theme.rounded,
              theme.card
            )}
          >
            <div className="flex justify-between items-start mb-4">
              <div className={cn("p-3 rounded-2xl", theme.accent, theme.accentText)}>
                <Percent className="w-6 h-6" />
              </div>
              <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button 
                  onClick={() => openModal(tax)}
                  className="p-2 hover:bg-black/5 rounded-lg transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDelete(tax.id)}
                  className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <h3 className="text-2xl font-bold mb-1">{tax.name}</h3>
            <div className="flex items-baseline gap-1 mb-3 justify-between">
              <span className="text-4xl font-black tracking-tighter">{tax.rate}%</span>
              <span className="text-[10px] uppercase tracking-widest font-bold opacity-50 bg-black/5 px-2 py-1 rounded">
                {tax.type || 'Both'}
              </span>
            </div>
            
            {tax.description && (
              <p className="text-sm opacity-50 italic serif line-clamp-2 mb-3">
                {tax.description}
              </p>
            )}

            {tax.mappedAccountId && accounts.find(a => a.id === tax.mappedAccountId) && (
              <div className="mt-4 pt-4 border-t border-black/10">
                <div className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-1">
                  {language === 'ar' ? 'الحساب المرتبط' : 'Mapped Account'}
                </div>
                <div className="text-sm font-bold truncate">
                  {accounts.find(a => a.id === tax.mappedAccountId)?.code} - {accounts.find(a => a.id === tax.mappedAccountId)?.name}
                </div>
              </div>
            )}
          </div>
        ))}

        {filteredRates.length === 0 && (
          <div className="col-span-full py-20 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-black/5 mb-4">
              <AlertCircle className="w-10 h-10 opacity-20" />
            </div>
            <h3 className="text-xl font-bold opacity-40 italic serif">
              {language === 'ar' ? 'لا توجد معدلات ضريبة القيمة المضافة' : 'No VAT rates found'}
            </h3>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-black/60 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModal();
          }}
        >
          <div className={cn("w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain p-8 border-4 bg-white", theme.border, theme.rounded)}>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold italic serif">
                {editingTax 
                  ? (language === 'ar' ? 'تعديل الضريبة' : 'Edit VAT Rate')
                  : (language === 'ar' ? 'إضافة ضريبة' : 'Add VAT Rate')
                }
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-black/5 rounded-full transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest opacity-50 mb-2">
                  {language === 'ar' ? 'الاسم *' : 'Name *'}
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-4 border-2 border-[#141414] rounded-xl outline-none focus:ring-4 focus:ring-[#141414]/10 transition-all font-bold"
                  placeholder={language === 'ar' ? 'مثال: ضريبة القيمة المضافة' : 'e.g. VAT'}
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest opacity-50 mb-2">
                  {language === 'ar' ? 'النوع *' : 'Type *'}
                </label>
                <select
                  required
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full p-4 border-2 border-[#141414] rounded-xl outline-none focus:ring-4 focus:ring-[#141414]/10 transition-all font-bold bg-white appearance-none"
                >
                  <option value="both">{language === 'ar' ? 'المبيعات والمشتريات' : 'Sales & Purchases'}</option>
                  <option value="sales">{language === 'ar' ? 'المبيعات فقط' : 'Sales Only'}</option>
                  <option value="purchases">{language === 'ar' ? 'المشتريات فقط' : 'Purchases Only'}</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest opacity-50 mb-2">
                  {language === 'ar' ? 'المعدل (%) *' : 'Rate (%) *'}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    required
                    step="0.01"
                    value={isNaN(formData.rate) ? '' : formData.rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, rate: e.target.value === '' ? 0 : parseFloat(e.target.value) }))}
                    className="w-full p-4 border-2 border-[#141414] rounded-xl outline-none focus:ring-4 focus:ring-[#141414]/10 transition-all font-bold pr-12"
                  />
                  <Percent className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 opacity-40" />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest opacity-50 mb-2">
                  {language === 'ar' ? 'الوصف' : 'Description'}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-4 border-2 border-[#141414] rounded-xl outline-none focus:ring-4 focus:ring-[#141414]/10 transition-all font-bold min-h-[100px]"
                  placeholder={language === 'ar' ? 'تفاصيل إضافية...' : 'Additional details...'}
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest opacity-50 mb-2">
                  {language === 'ar' ? 'حساب الضريبة المرتبط' : 'Mapped Tax Account'}
                </label>
                <select
                  value={formData.mappedAccountId}
                  onChange={(e) => setFormData(prev => ({ ...prev, mappedAccountId: e.target.value }))}
                  className="w-full p-4 border-2 border-[#141414] rounded-xl outline-none focus:ring-4 focus:ring-[#141414]/10 transition-all font-bold bg-white appearance-none"
                >
                  <option value="">{language === 'ar' ? 'لا يوجد حساب مرتبط' : 'No account mapped'}</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.code} - {acc.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-6 py-4 border-2 border-[#141414] font-bold uppercase tracking-widest rounded-xl hover:bg-black/5 transition-all"
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className={cn(
                    "flex-1 px-6 py-4 font-bold uppercase tracking-widest transition-all hover:scale-105 active:scale-95",
                    theme.accent,
                    theme.accentText,
                    theme.rounded
                  )}
                >
                  {editingTax 
                    ? (language === 'ar' ? 'تحديث' : 'Update')
                    : (language === 'ar' ? 'حفظ' : 'Save')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
